import { ClientOnly, createFileRoute, isNotFound, redirect } from '@tanstack/react-router';
import * as v from 'valibot';
import { usePortalCampaignsQuery, usePortalMetadataQuery } from '@/lib/data-hooks';
import { Campaigns } from './-components/campaigns';
import { Hero } from './-components/hero';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { lazy, Suspense, useEffect } from 'react';
import { buildAlternates, Seo } from '../-components/seo';
import { useEphemeralStore } from '@/lib/app-store';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { FilterPanelProvider, FilterPanel } from './-components/filter-panel';
import { CampaignsFiltersSidebar } from './-components/campaigns-filters-sidebar';
import { preloadImageOnClient, heroImageSize, campaignImageSize } from '@worldcoo/ui/lib/utils';
import { ensureSeoData } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { isServer } from '@tanstack/react-query';
import { stripHtml } from '@worldcoo/shared/utils';
import { hasLocalePrefix } from '@/router';

const TestimonialSlider = lazy(() =>
	import('./-components/testimonial-slider').then((m) => ({ default: m.TestimonialSlider })),
);
const FaqSection = lazy(() => import('./-components/faq-section').then((m) => ({ default: m.FaqSection })));

export const Route = createFileRoute('/$portalId/')({
	validateSearch: v.object({
		q: v.fallback(v.optional(v.string()), undefined),
		year: v.fallback(v.optional(v.number()), undefined),
		state: v.fallback(v.optional(v.literal('finished')), undefined),
		causes: v.fallback(
			v.optional(
				v.array(
					v.picklist([
						'basicNeeds',
						'humanRights',
						'education',
						'emergency',
						'feeding',
						'environment',
						'habitat',
						'health',
					]),
				),
			),
			undefined,
		),
	}),
	loaderDeps: ({ search: { q, year, state, causes, city, iframe } }) => ({
		q,
		year,
		causes,
		state,
		city,
		iframe,
	}),
	loader: async ({ context, deps, params }) => {
		context.queryClient
			.ensureQueryData(trpc.portal.getAuthor.queryOptions({ portalId: context.portalId, locale: context.locale }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				const logoHeight = getLogoImageSize(context.portalId).height;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: logoHeight,
						width: Math.round(logoHeight * (data.logo.width / data.logo.height)),
						format: 'avif',
						quality: 60,
					}),
				);
			});

		await ensureSeoData(
			context,
			context.queryClient
				.ensureQueryData(trpc.portal.getMetadata.queryOptions({ portalId: context.portalId, locale: context.locale }))
				.then((data) => {
					if ('notFound' in data) return;
					if (!data.media?.location) return;
					preloadImageOnClient(
						generateImageSrc(data.media.location, { ...heroImageSize, format: 'avif', quality: 60 }),
					);
				}),
		);

		context.queryClient.ensureQueryData(
			trpc.portal.getCampaignsAggregates.queryOptions({
				portalId: context.portalId,
				locale: context.locale,
				query: deps.q,
				year: deps.year,
				state: deps.state,
				causes: deps.causes,
				city: deps.city,
			}),
		);

		const portalCampaignsPromise = context.queryClient
			.ensureQueryData(
				trpc.portal.getCampaigns.queryOptions({
					portalId: context.portalId,
					locale: context.locale,
					query: deps.q,
					year: deps.year,
					state: deps.state,
					causes: deps.causes,
					city: deps.city,
				}),
			)
			.then((data) => {
				if ('notFound' in data) return;

				// If we're using standalone donations and we have city and only one campaign, redirect to it
				if (isServer && deps.iframe && deps.city && data.campaigns.length === 1) {
					throw redirect({
						to: '/$portalId/$campaignId',
						params: {
							portalId: params.portalId,
							campaignId: data.campaigns[0]!.id,
						},
						search: (prev) => prev,
					});
				}

				let count = 0;
				for (const campaign of data.campaigns) {
					if (!campaign.media?.location) continue;
					preloadImageOnClient(
						generateImageSrc(campaign.media.location, { ...campaignImageSize, format: 'avif', quality: 60 }),
					);
					if (++count === 6) break;
				}
			});

		if (isServer && deps.iframe && deps.city) {
			await portalCampaignsPromise;
		}
	},
	component: Component,
});

function Component() {
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const setPagesLoad = useEphemeralStore((store) => store.setPagesLoaded);
	useEffect(() => {
		if (pagesLoaded.includes('home')) return;
		const timeout = setTimeout(() => setPagesLoad([...pagesLoaded, 'home']), 1500);
		return () => clearTimeout(timeout);
	}, [pagesLoaded, setPagesLoad]);

	return (
		<FilterPanelProvider>
			<Suspense>
				<SuspensedHead />
			</Suspense>

			<div className="flex min-h-svh w-full">
				<main className="flex min-w-0 flex-1 flex-col">
					<Header />
					<section className="mx-auto w-full max-w-[1366px]">
						<Hero />
					</section>

					<div id="campaigns" className="bg-secondary border-border flex-1 border-t">
						<main className="mx-auto flex max-w-[1366px] flex-col gap-6 px-6 py-16 lg:gap-12 lg:py-24">
							<Campaigns />
						</main>
					</div>

					<Suspense>
						<ConditionalTestimonialSlider />
					</Suspense>

					<Suspense>
						<ConditionalFaqSection />
					</Suspense>

					<Footer />
				</main>
				<ClientOnly>
					<FilterPanel>
						<CampaignsFiltersSidebar />
					</FilterPanel>
				</ClientOnly>
			</div>
		</FilterPanelProvider>
	);
}

function ConditionalTestimonialSlider() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;
	if (!metadata.sliderItems || metadata.sliderItems.length === 0) return null;
	return <TestimonialSlider />;
}

function ConditionalFaqSection() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;
	if (!metadata.faqItems || metadata.faqItems.length === 0) return null;
	return <FaqSection />;
}

function SuspensedHead() {
	const { baseUrl, locale } = Route.useRouteContext();
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	// Match the page's actual URL: at `/` (unprefixed root) the canonical points to
	// `${baseUrl}` so it matches the x-default hreflang. At `/${locale}` it points to
	// the prefixed URL. Lighthouse flags the canonical as invalid when it matches a
	// non-self hreflang alternate.
	const canonicalUrl = hasLocalePrefix() ? `${baseUrl}/${locale}` : baseUrl;
	const logoUrl = metadata.media?.location ? generateImageSrc(metadata.media.location) : undefined;

	// Organization schema
	const organizationLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		'name': metadata.texts.title,
		'url': baseUrl,
		...(metadata.texts.presentation && { description: stripHtml(metadata.texts.presentation) }),
		...(logoUrl && { logo: logoUrl }),
	};

	// WebSite schema with SearchAction. The campaigns listing accepts a `q` query param
	// that filters by campaign name / NGO name / id — see the `validateSearch` at the top
	// of this route. `target` uses the EntryPoint form Google requires for the sitelinks
	// searchbox feature.
	const webSiteLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		'name': metadata.texts.title,
		'url': baseUrl,
		'potentialAction': {
			'@type': 'SearchAction',
			'target': {
				'@type': 'EntryPoint',
				'urlTemplate': `${baseUrl}/${locale}?q={search_term_string}`,
			},
			'query-input': 'required name=search_term_string',
		},
	};

	// FAQPage schema (only when FAQ items are present)
	const faqLd =
		metadata.faqItems.length > 0
			? {
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					'mainEntity': metadata.faqItems.map((item) => ({
						'@type': 'Question',
						'name': item.question,
						'acceptedAnswer': { '@type': 'Answer', 'text': item.answer },
					})),
				}
			: undefined;

	// ItemList schema for campaigns (enables rich carousel results)
	const campaignsData = usePortalCampaignsQuery();
	const campaigns = isNotFound(campaignsData) ? [] : campaignsData.campaigns;
	const itemListLd =
		campaigns.length > 0
			? {
					'@context': 'https://schema.org',
					'@type': 'ItemList',
					'name': metadata.texts.title,
					'itemListElement': campaigns.map(
						(campaign: { id: string; slugsByLocale: Record<string, string>; name?: string }, i: number) => ({
							'@type': 'ListItem',
							'position': i + 1,
							'name': campaign.name,
							'url': `${baseUrl}/${locale}/${campaign.slugsByLocale[locale] ?? campaign.id}`,
						}),
					),
				}
			: undefined;

	// Review schema for testimonials
	const reviewsLd =
		metadata.sliderItems.length > 0
			? metadata.sliderItems.map((item) => ({
					'@context': 'https://schema.org',
					'@type': 'Review',
					'author': { '@type': 'Person', 'name': item.authorName },
					'reviewBody': item.quote,
					'itemReviewed': {
						'@type': 'Organization',
						'name': metadata.texts.title,
					},
				}))
			: [];

	return (
		<Seo
			title={metadata.texts.title}
			description={metadata.texts.presentation || metadata.texts.title}
			image={logoUrl}
			imageWidth={metadata.media?.width}
			imageHeight={metadata.media?.height}
			imageType={metadata.media?.type}
			imageAlt={metadata.imageAlt}
			canonicalUrl={canonicalUrl}
			locale={locale}
			alternateLocales={metadata.locales}
			siteName={metadata.texts.title}
			alternates={buildAlternates(baseUrl, '/', metadata.locales)}
			jsonLd={[organizationLd, webSiteLd, faqLd, itemListLd, ...reviewsLd].filter((v) => v !== undefined)}
		/>
	);
}
